import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { Share2, MessageCircle, MessageSquare, Link2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "../lib/apiClient";

// Share a deal to its SIBLINGO deal page via WhatsApp, text message, or copy link.
// The menu is rendered in a portal with fixed coords so card `overflow-hidden`/transforms never clip it.
export default function ShareButton({ dealId, title, store, price, was, variant = "icon", className = "" }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);

  // Server-rendered preview URL (Open Graph tags) that redirects real users to /deal/:id —
  // this makes WhatsApp/iMessage show the deal's image + title.
  const url = `${API}/share/${dealId}`;

  // Warm, ready-to-drop caption for community WhatsApp groups.
  const meta = [store, price ? `$${Number(price).toFixed(2)}` : ""].filter(Boolean).join(" · ");
  const caption =
    `A beautiful family collection on SIBLINGO ✨\n` +
    `${title || "This one"}${meta ? ` (${meta})` : ""}\n` +
    `Sharing in case it helps someone here 💕\n${url}`;
  const full = caption;

  const place = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const width = 180;
    let left = r.right - width;
    if (left < 8) left = 8;
    if (left + width > window.innerWidth - 8) left = window.innerWidth - width - 8;
    setPos({ top: r.bottom + 6, left });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    place();
    const onScroll = () => setOpen(false);
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, place]);

  const stop = (e) => e.stopPropagation();
  const toggle = (e) => { stop(e); setOpen((o) => !o); };
  const whatsapp = (e) => { stop(e); window.open(`https://wa.me/?text=${encodeURIComponent(full)}`, "_blank", "noopener"); setOpen(false); };
  const sms = (e) => { stop(e); window.location.href = `sms:?&body=${encodeURIComponent(full)}`; setOpen(false); };
  const copy = async (e) => {
    stop(e);
    try { await navigator.clipboard.writeText(url); toast.success("Link copied!"); }
    catch { toast.error("Couldn't copy the link"); }
    setOpen(false);
  };

  return (
    <div className={`relative ${className}`} onClick={stop}>
      <button
        ref={btnRef}
        onClick={toggle}
        data-testid={`share-btn-${dealId}`}
        aria-label={title ? `Share ${title}` : "Share this collection"}
        className={variant === "full"
          ? "inline-flex items-center justify-center gap-2 h-11 px-4 rounded-full border-2 border-line text-ink-soft font-bold text-sm hover:border-brand/40 hover:text-brand transition-all"
          : "inline-flex items-center justify-center h-11 w-11 rounded-full bg-white border-2 border-line text-ink-soft hover:text-brand hover:border-brand/40 transition-all shrink-0"}
      >
        <Share2 size={18} /> {variant === "full" && "Share"}
      </button>
      {open && ReactDOM.createPortal(
        <>
          <div className="fixed inset-0 z-[95]" onClick={(e) => { stop(e); setOpen(false); }} data-testid={`share-overlay-${dealId}`} />
          <div
            data-testid={`share-menu-${dealId}`}
            style={{ position: "fixed", top: pos.top, left: pos.left, width: 180 }}
            className="z-[96] bg-white rounded-2xl border border-line shadow-lift p-1.5"
            onClick={stop}
          >
            <button onClick={whatsapp} data-testid={`share-whatsapp-${dealId}`} className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-semibold text-ink hover:bg-blush transition-colors"><MessageCircle size={16} className="text-[#25D366]" /> WhatsApp</button>
            <button onClick={sms} data-testid={`share-sms-${dealId}`} className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-semibold text-ink hover:bg-blush transition-colors"><MessageSquare size={16} className="text-brand" /> Text message</button>
            <button onClick={copy} data-testid={`share-copy-${dealId}`} className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-semibold text-ink hover:bg-blush transition-colors"><Link2 size={16} className="text-ink-soft" /> Copy link</button>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}
